import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Unit } from '@app/src/types/resourceExplorer'
import CheckIcon from '@mui/icons-material/Check'
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import type { FilterCrumb } from '../filters/FilterBreadcrumbs'
import FilterBreadcrumbs from '../filters/FilterBreadcrumbs'
import { menuOptionSx } from '../filters/filterGroupDisplay'
import type { FilterEditorHeader, MrtCountTarget } from '../mrtCollectionTypes'
import RangeBody from '../filters/RangeBody'
import type { SpendFilterValue } from './spendFilterValue'
import { ANY_PERIOD, NO_SPEND_COUNT_KEY, normaliseSpendFilterValue, spendFilterAsksSomething } from './spendFilterValue'
import useSpendGroupCounts from './useSpendGroupCounts'
import type { SpendFilterOptions } from './useSpendFilterOptions'

type SpendFilterEditorProps = {
  options: SpendFilterOptions
  value: unknown
  onChange: (value: SpendFilterValue | undefined) => void
  currencyUnit?: Unit
  // In the grouped dropdown, which leaves the header to this editor so the period continues its breadcrumb.
  header?: FilterEditorHeader
  // The filters that narrow the spend group counts, and where they are counted (the providers without it).
  countFilterGroups?: FilterGroup[]
  countTarget?: MrtCountTarget
}

// No spend sits in the group list but is its own mode: "High or no spend" is two spends, and the backend
// matches a selection against one.
type GroupOrInterval = 'group' | 'customInterval'

const PERIOD_LIST_MAX_HEIGHT = 30

// The option's label with its count pushed to the far edge, as the value lists show theirs.
const groupOptionLabel = (label: string, count: number | undefined, isCountLoading: boolean) => ({
  disableTypography: true,
  sx: { mr: 0 },
  label: (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ flex: 1 }}>
      <Typography variant="body1">{label}</Typography>
      {count !== undefined ? (
        <Typography variant="body2" color="text.secondary">
          {count}
        </Typography>
      ) : (
        isCountLoading && <CircularProgress size={12} />
      )}
    </Stack>
  ),
})

type StepHeaderProps = { isChoosingPeriod: boolean; periodLabel: string; onShowPeriodStep: () => void }

// The column crumb leads back to the period step, as a multi-step filter's leads to its first step.
const GroupedStepHeader: React.FC<StepHeaderProps & { header: FilterEditorHeader }> = ({
  header,
  isChoosingPeriod,
  periodLabel,
  onShowPeriodStep,
}) => {
  const { formatMessage } = useIntl()
  const columnCrumb: FilterCrumb = { label: header.columnLabel, onClick: onShowPeriodStep }
  return (
    <>
      <FilterBreadcrumbs
        crumbs={
          isChoosingPeriod ? [...header.crumbs, columnCrumb] : [...header.crumbs, columnCrumb, { label: periodLabel }]
        }
      />
      <Typography variant="subtitle1">
        {isChoosingPeriod ? formatMessage({ id: 'schemas.filter.spendPeriod' }) : header.columnLabel}
      </Typography>
    </>
  )
}

const PlainStepHeader: React.FC<StepHeaderProps> = ({ isChoosingPeriod, periodLabel, onShowPeriodStep }) => {
  const { formatMessage } = useIntl()
  const spendPeriodLabel = formatMessage({ id: 'schemas.filter.spendPeriod' })
  return isChoosingPeriod ? (
    <Typography variant="body2" color="text.secondary">
      {spendPeriodLabel}
    </Typography>
  ) : (
    <FilterBreadcrumbs crumbs={[{ label: spendPeriodLabel, onClick: onShowPeriodStep }, { label: periodLabel }]} />
  )
}

/**
 * Two steps, as the risk and assessment filters: the spend period, with the default ticked, then what the spend is - a
 * spend group, no spend, or a custom interval. An applied selection opens on the second.
 *
 * The whole selection is one value so the backend matches it against a single spend rather than several
 * unrelated ones. Held in local state so a half-built selection stays on screen; a selection that asks
 * nothing yet emits undefined, which the commit path treats as no filter.
 */
const SpendFilterEditor: React.FC<SpendFilterEditorProps> = ({
  options,
  value,
  onChange,
  currencyUnit,
  header,
  countFilterGroups = [],
  countTarget,
}) => {
  const { formatMessage } = useIntl()
  const [selection, setSelection] = useState<SpendFilterValue>(() => normaliseSpendFilterValue(value) ?? {})
  const [isChoosingPeriod, setIsChoosingPeriod] = useState(
    () => !spendFilterAsksSomething(normaliseSpendFilterValue(value)),
  )

  // The options come from a facet request that can still be in flight when the editor opens, so the
  // preselection is resolved on every render rather than frozen into the initial state - otherwise an
  // editor opened first would keep a blank period after the default one arrived.
  const periodName = selection.periodName ?? options.defaultPeriodName
  const mode = selection.mode ?? 'interval'
  const groupOrInterval: GroupOrInterval = mode === 'amount' ? 'customInterval' : 'group'
  const { spendClassifications, amountRange } = selection
  const { countByGroup, isLoading: isCountLoading } = useSpendGroupCounts(
    periodName,
    options.spendClassifications.map(classification => classification.value),
    countFilterGroups,
    countTarget,
  )

  // Normalising the patched selection is what drops the parts the newly chosen mode does not ask about,
  // so switching mode cannot carry the previous one's answer into the value that gets stored.
  const update = (patch: Partial<SpendFilterValue>): void => {
    const next = normaliseSpendFilterValue({ ...selection, periodName, mode, ...patch }) ?? {}
    setSelection(next)
    onChange(next.periodName && spendFilterAsksSomething(next) ? next : undefined)
  }

  const periodLabel = (period?: string): string =>
    period === ANY_PERIOD ? formatMessage({ id: 'schemas.filter.spendAnyPeriod' }) : (period ?? '')

  const toggleClassification = (classification: string): void => {
    const chosen = mode === 'interval' ? (spendClassifications ?? []) : []
    update({
      mode: 'interval',
      spendClassifications: chosen.includes(classification)
        ? chosen.filter(entry => entry !== classification)
        : [...chosen, classification],
    })
  }

  const choosePeriod = (period: string): void => {
    update({ periodName: period })
    setIsChoosingPeriod(false)
  }

  const stepHeaderProps = {
    isChoosingPeriod,
    periodLabel: periodLabel(periodName),
    onShowPeriodStep: () => setIsChoosingPeriod(true),
  }
  const headerElement = header ? (
    <GroupedStepHeader header={header} {...stepHeaderProps} />
  ) : (
    <PlainStepHeader {...stepHeaderProps} />
  )

  if (isChoosingPeriod)
    return (
      <Stack spacing={1}>
        {headerElement}
        <Stack sx={theme => ({ maxHeight: theme.spacing(PERIOD_LIST_MAX_HEIGHT), overflowY: 'auto' })}>
          <MenuList>
            {[ANY_PERIOD, ...options.periods.map(period => period.value)].map(period => (
              <MenuItem
                key={period}
                selected={period === periodName}
                onClick={() => choosePeriod(period)}
                sx={menuOptionSx}
              >
                <ListItemText>{periodLabel(period)}</ListItemText>
                {period === periodName && (
                  <ListItemIcon>
                    <CheckIcon fontSize="small" />
                  </ListItemIcon>
                )}
              </MenuItem>
            ))}
          </MenuList>
        </Stack>
      </Stack>
    )

  return (
    <Stack spacing={1.5}>
      {headerElement}
      <RadioGroup
        value={groupOrInterval}
        onChange={(_event, next) => update({ mode: next === 'customInterval' ? 'amount' : 'interval' })}
      >
        <FormControlLabel
          value="group"
          // Aligned to the top so the radio sits on the label's first line rather than between the two lines.
          sx={{ alignItems: 'flex-start' }}
          control={<Radio size="small" sx={{ pt: 0.25 }} />}
          disabled={!periodName}
          disableTypography
          label={
            <Stack>
              <Typography variant="body1">{formatMessage({ id: 'schemas.filter.spendGroup' })}</Typography>
              {groupOrInterval === 'group' && (
                <Typography variant="body2" color="text.secondary">
                  {formatMessage({ id: 'schemas.filter.spendGroupDescription' })}
                </Typography>
              )}
            </Stack>
          }
        />
        {groupOrInterval === 'group' && (
          // The list scrolls, so it needs left padding of its own: a control's ripple reaches 11px past the
          // label box and the scroll container would otherwise clip it.
          <Stack sx={{ maxHeight: theme => theme.spacing(24), overflowY: 'auto', px: 4 }}>
            {options.spendClassifications.map(classification => (
              <FormControlLabel
                key={classification.value}
                control={
                  <Checkbox
                    size="small"
                    disabled={!periodName}
                    checked={mode === 'interval' && Boolean(spendClassifications?.includes(classification.value))}
                    onChange={() => toggleClassification(classification.value)}
                  />
                }
                {...groupOptionLabel(classification.label, countByGroup.get(classification.value), isCountLoading)}
              />
            ))}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  disabled={!periodName}
                  checked={mode === 'notAdded'}
                  onChange={() => update({ mode: mode === 'notAdded' ? 'interval' : 'notAdded' })}
                />
              }
              {...groupOptionLabel(
                formatMessage({ id: 'dashboard.sourcing.overallRisk.noSpend' }),
                countByGroup.get(NO_SPEND_COUNT_KEY),
                isCountLoading,
              )}
            />
          </Stack>
        )}
        <FormControlLabel
          value="customInterval"
          control={<Radio size="small" />}
          disabled={!periodName}
          label={formatMessage({ id: 'schemas.filter.spendCustomInterval' })}
        />
        {groupOrInterval === 'customInterval' && (
          <Stack sx={{ pl: 4 }}>
            <RangeBody
              value={amountRange}
              onChange={next => update({ amountRange: next })}
              currencyUnit={currencyUnit}
              disabled={!periodName}
            />
          </Stack>
        )}
      </RadioGroup>
    </Stack>
  )
}

export default SpendFilterEditor
